import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HeartPulse, FileText, Stethoscope, MessageSquareText, ShieldCheck } from "lucide-react";

const STEPS = [
  {
    icon: FileText,
    title: "Share your concern",
    desc: "Describe what's worrying you and attach any test results or reports you already have.",
  },
  {
    icon: Stethoscope,
    title: "A specialist reviews it",
    desc: "An experienced specialist reviews your case and prepares a written second opinion.",
  },
  {
    icon: MessageSquareText,
    title: "Talk it through",
    desc: "Get clear points and questions to discuss with your own doctor — and book a local consultation if you'd like.",
  },
];

export const CARE_WELCOME_KEY = "pd_care_welcome_seen";

export default function CareWelcome() {
  const navigate = useNavigate();
  const finish = () => {
    localStorage.setItem(CARE_WELCOME_KEY, "1");
    navigate("/care", { replace: true });
  };

  return (
    <div className="care-scope min-h-screen w-full flex justify-center bg-black">
      <div className="relative w-full max-w-[430px] h-screen bg-[#111111] flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto no-scrollbar px-6 pt-10 pb-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#EF9F27] text-[#111111] mb-6"
          >
            <HeartPulse size={30} strokeWidth={2.2} />
          </motion.div>

          <h1 className="font-head text-3xl font-extrabold text-[#F8FAFC] leading-tight">
            Welcome to Care
          </h1>
          <p className="text-[#A1A1AA] mt-2 leading-relaxed">
            Get a trusted specialist's second opinion on a health concern — from wherever you are.
            Here's how it works.
          </p>

          <div className="mt-8 space-y-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.1 + i * 0.1 }}
                  data-testid={`care-welcome-step-${i}`}
                  className="bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5 flex gap-4"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#EF9F27]/15 flex items-center justify-center shrink-0">
                    <Icon size={22} className="text-[#EF9F27]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#EF9F27]">Step {i + 1}</span>
                    </div>
                    <h2 className="font-head text-lg font-bold text-[#F8FAFC] mt-0.5">{s.title}</h2>
                    <p className="text-sm text-[#A1A1AA] mt-1 leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-6 bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-xl p-4 flex gap-3">
            <ShieldCheck size={18} className="text-[#EF9F27] shrink-0 mt-0.5" />
            <p className="text-sm text-[#EFC77A] leading-relaxed">
              A second opinion is information to discuss with your doctor. It is not a diagnosis,
              prescription, or a replacement for in-person care. In an emergency, call your local
              emergency services.
            </p>
          </div>
        </div>

        <div className="shrink-0 px-6 pb-8 pt-4 bg-[#111111] border-t border-[#333333]">
          <button
            data-testid="care-welcome-start-btn"
            onClick={finish}
            className="w-full rounded-full bg-[#EF9F27] text-[#111111] font-bold px-6 py-4 min-h-[52px] transition-transform active:scale-[0.97]"
          >
            Get started
          </button>
        </div>
      </div>
    </div>
  );
}
